import { lazy } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { AppFrame } from "@/features/shell/AppFrame";
import { RequireSession } from "@/features/session/RequireSession";
import { Root } from "./Root";

// Viste legacy: restano lazy come in App.jsx; ogni fase successiva ne sostituisce un gruppo.
const HomeLayout = lazy(() => import("@/features/auth/HomeLayout").then((m) => ({ default: m.HomeLayout })));
const LoginPage = lazy(() => import("@/features/auth/LoginPage").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import("@/features/auth/SignupPage").then((m) => ({ default: m.SignupPage })));
const ActivationPage = lazy(() =>
  import("@/features/auth/ActivationPage").then((m) => ({ default: m.ActivationPage })),
);
const ActivationMailPage = lazy(() =>
  import("@/features/auth/ActivationMailPage").then((m) => ({ default: m.ActivationMailPage })),
);
const CmsLoginPage = lazy(() =>
  import("@/features/auth/CmsLoginPage").then((m) => ({ default: m.CmsLoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/features/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/features/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const Wizard = lazy(() => import("@/legacy/views/Wizard/Wizard"));
const WizardStart = lazy(() => import("@/legacy/views/Wizard/WizardStart/WizardStart"));
const WizardCms = lazy(() => import("@/legacy/views/Wizard/WizardCms/WizardCms"));
const WizardShop = lazy(() => import("@/legacy/views/Wizard/WizardShop/WizardShop"));
const WizardSettings = lazy(() => import("@/legacy/views/Wizard/WizardSettings/WizardSettings"));
const AccountPage = lazy(() =>
  import("@/features/account/AccountPage").then((m) => ({ default: m.AccountPage })),
);
const BillingLayout = lazy(() =>
  import("@/features/billing/BillingLayout").then((m) => ({ default: m.BillingLayout })),
);
const BillingOverview = lazy(() =>
  import("@/features/billing/BillingOverviewPage").then((m) => ({ default: m.BillingOverviewPage })),
);
const BillingPlans = lazy(() =>
  import("@/features/billing/BillingPlansPage").then((m) => ({ default: m.BillingPlansPage })),
);
const BillingInformation = lazy(() =>
  import("@/features/billing/BillingInformationPage").then((m) => ({ default: m.BillingInformationPage })),
);
const SupportPage = lazy(() =>
  import("@/features/support/SupportPage").then((m) => ({ default: m.SupportPage })),
);
const FineTuningPage = lazy(() =>
  import("@/features/fine-tuning/FineTuningPage").then((m) => ({ default: m.FineTuningPage })),
);
const CampaignsPage = lazy(() =>
  import("@/features/visual-machine/campaigns/CampaignsPage").then((m) => ({ default: m.CampaignsPage })),
);
const Banners = lazy(() => import("@/legacy/views/VisualMachine/Banners/Banners"));
const Recommendations = lazy(() => import("@/legacy/views/VisualMachine/Recommendations/Recommendations"));
const VisualMachineLayout = lazy(() =>
  import("@/features/visual-machine/VisualMachineLayout").then((m) => ({ default: m.VisualMachineLayout })),
);
const LayoutDesignLayout = lazy(() =>
  import("@/features/layout-design/LayoutDesignLayout").then((m) => ({ default: m.LayoutDesignLayout })),
);
const GeneralPage = lazy(() =>
  import("@/features/layout-design/general/GeneralPage").then((m) => ({ default: m.GeneralPage })),
);
const DevicePage = lazy(() =>
  import("@/features/layout-design/device/DevicePage").then((m) => ({ default: m.DevicePage })),
);
const CardPage = lazy(() =>
  import("@/features/layout-design/CardPage").then((m) => ({ default: m.CardPage })),
);
const BuilderPage = lazy(() =>
  import("@/features/layout-design/BuilderPage").then((m) => ({ default: m.BuilderPage })),
);
const CustomStylePage = lazy(() =>
  import("@/features/layout-design/CustomStylePage").then((m) => ({ default: m.CustomStylePage })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const AnalyticsLayout = lazy(() =>
  import("@/features/analytics/AnalyticsLayout").then((m) => ({ default: m.AnalyticsLayout })),
);
const AnalyticsOverviewPage = lazy(() =>
  import("@/features/analytics/AnalyticsOverviewPage").then((m) => ({ default: m.AnalyticsOverviewPage })),
);
const AnalyticsFiltersPage = lazy(() =>
  import("@/features/analytics/AnalyticsFiltersPage").then((m) => ({ default: m.AnalyticsFiltersPage })),
);
const AnalyticsQueriesPage = lazy(() =>
  import("@/features/analytics/AnalyticsQueriesPage").then((m) => ({ default: m.AnalyticsQueriesPage })),
);
const SetupLayout = lazy(() =>
  import("@/features/setup/SetupLayout").then((m) => ({ default: m.SetupLayout })),
);
const SearchConfigurationPage = lazy(() =>
  import("@/features/setup/SearchConfigurationPage").then((m) => ({ default: m.SearchConfigurationPage })),
);
const AdvancedSettingsPage = lazy(() =>
  import("@/features/setup/AdvancedSettingsPage").then((m) => ({ default: m.AdvancedSettingsPage })),
);
const TermsPage = lazy(() => import("@/features/terms/TermsPage").then((m) => ({ default: m.TermsPage })));
const NotFoundPage = lazy(() =>
  import("@/features/shell/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);

export const routes: RouteObject[] = [
  {
    element: <Root />,
    children: [
      {
        element: <HomeLayout />,
        children: [
          { path: "/", element: <LoginPage /> },
          { path: "/signup", element: <SignupPage /> },
          { path: "/activation", element: <ActivationPage /> },
          { path: "/crm-login", element: <CmsLoginPage /> },
          { path: "/activation-mail", element: <ActivationMailPage /> },
          { path: "/forgot-password", element: <ForgotPasswordPage /> },
          { path: "/recovery", element: <ResetPasswordPage /> },
        ],
      },
      {
        element: (
          <RequireSession>
            <AppFrame />
          </RequireSession>
        ),
        children: [
          {
            path: "/wizard",
            element: <Wizard />,
            children: [
              { index: true, element: <WizardStart /> },
              { path: "cms", element: <WizardCms /> },
              { path: "shop", element: <WizardShop /> },
              { path: "settings", element: <WizardSettings /> },
            ],
          },
          { path: "/account", element: <AccountPage /> },
          {
            path: "/billing",
            element: <BillingLayout />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: "overview", element: <BillingOverview /> },
              { path: "plans", element: <BillingPlans /> },
              { path: "information", element: <BillingInformation /> },
            ],
          },
          { path: "/support", element: <SupportPage /> },
          { path: "/fineTuning", element: <FineTuningPage /> },
          {
            path: "/visualMachine",
            element: <VisualMachineLayout />,
            children: [
              { index: true, element: <Navigate to="campaigns" replace /> },
              { path: "campaigns", element: <CampaignsPage /> },
              { path: "banners", element: <Banners /> },
              { path: "recommendations", element: <Recommendations /> },
            ],
          },
          {
            path: "/layoutDesign",
            element: <LayoutDesignLayout />,
            children: [
              { index: true, element: <Navigate to="general" replace /> },
              { path: "general", element: <GeneralPage /> },
              { path: "desktop", element: <DevicePage device="desktop" /> },
              { path: "mobile", element: <DevicePage device="mobile" /> },
              { path: "card", element: <CardPage /> },
              { path: "builder", element: <BuilderPage /> },
              { path: "customStyle", element: <CustomStylePage /> },
            ],
          },
          { path: "/dashboard", element: <DashboardPage /> },
          {
            path: "/analytics",
            element: <AnalyticsLayout />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: "overview", element: <AnalyticsOverviewPage /> },
              { path: "filters", element: <AnalyticsFiltersPage /> },
              { path: "queries", element: <AnalyticsQueriesPage /> },
            ],
          },
          {
            path: "/setup",
            element: <SetupLayout />,
            children: [
              { index: true, element: <Navigate to="search" replace /> },
              { path: "search", element: <SearchConfigurationPage /> },
              { path: "settings", element: <AdvancedSettingsPage /> },
            ],
          },
          { path: "/terms", element: <TermsPage /> },
        ],
      },
      { path: "/notfound", element: <NotFoundPage /> },
      { path: "*", element: <Navigate to="/notfound" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
